import { tr } from '@/lib/i18n-core';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CircleDollarSign } from 'lucide-react';
import type { NeedsPriceSummary } from '@his/shared';
import { get } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/primitives';

export const NEEDS_PRICE_LINK = '/billing/charges?tab=charges&needsPrice=1';

export function useNeedsPrice() {
  const { can, hasModule } = useAuth();
  return useQuery({
    queryKey: ['charge-items', 'needs-price'],
    queryFn: () => get<NeedsPriceSummary>('/charge-items/needs-price'),
    enabled: hasModule('billing') && can('charge.manage'),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function UnpricedOrdersAlert({ onShow, className }: { onShow?: () => void; className?: string }) {
  const navigate = useNavigate();
  const summary = useNeedsPrice();
  const count = summary.data?.count ?? 0;
  if (count === 0) return null;
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-xl border border-warning/35 bg-warning/10 px-4 py-3 text-warning shadow-sm',
        className,
      )}
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-warning/15">
        <CircleDollarSign className="h-5 w-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1 basis-60">
        <p className="text-sm font-semibold">{tr('billing', 'orders_need_prices', { count })}</p>
        <p className="mt-0.5 text-xs text-foreground/70">{tr('billing', 'orders_need_prices_hint')}</p>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onShow ?? (() => navigate(NEEDS_PRICE_LINK))}>
        {tr('billing', 'show_services_without_price')}
        <ArrowRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
      </Button>
    </div>
  );
}
